import React from 'react';
import { createPortal } from 'react-dom';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getSubsectionHeadingSizeClass } from '../../utils/fontSizeUtils';

interface UpgradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'list' | 'ticker' | 'signup';
  hideButtons?: boolean; // If true, hide Cancel and Upgrade Plan buttons (for pro users at limit)
}

const UpgradeDialog: React.FC<UpgradeDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type,
  hideButtons = false
}) => {
  const { fontSize } = useFontSize();
  
  if (!isOpen) return null;

  const handleUpgradeClick = () => {
    // TODO: Implement upgrade flow
    console.log('Upgrade clicked for type:', type);
    onClose();
  };

  const handleSignUpClick = () => {
    // Navigate to signup page
    window.location.href = '/signup';
  };

  const dialogContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-background-card rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-6">
          <p className={`${getTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary`}>
            {message}
          </p>
        </div>

        {!hideButtons && (
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
            >
              Cancel
            </button>
            
            {type === 'signup' ? (
              <button
                onClick={handleSignUpClick}
                className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-medium text-gray-900 bg-primary rounded-md hover:bg-opacity-90 transition-colors`}
              >
                Sign Up
              </button>
            ) : (
              <button
                onClick={handleUpgradeClick}
                className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-medium text-gray-900 bg-primary rounded-md hover:bg-opacity-90 transition-colors`}
              >
                Upgrade Plan
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return isOpen ? createPortal(dialogContent, document.body) : null;
};

export default UpgradeDialog;
